import React from 'react';
import { useLang } from '@/i18n/LanguageContext';
import { motion } from 'framer-motion';
import { CalendarDays, MapPin, ArrowRight, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGoogleSheets } from '@/hooks/useGoogleSheets';
import { classData as fallbackClassData } from '@/data/classes';

const NextClasses = () => {
  const { lang } = useLang();
  const navigate = useNavigate();
  const { data: sheetsData, loading } = useGoogleSheets();

  // Helper to find next available class date
  const getNextAvailableDate = (campusKey) => {
    const dataSource = sheetsData || fallbackClassData;
    const campusData = dataSource?.[campusKey];
    if (!campusData) return 'Contact us for dates';

    // Check all types (hybrid, saturday, etc.) to find the next one that isn't full
    let featuredDate = null;
    let earliestValidDate = null;

    Object.values(campusData).forEach(typeArray => {
      typeArray.forEach(cls => {
        // Look for explicitly featured class
        if (cls.isFeatured && cls.startDate) {
          const date = new Date(cls.startDate);
          if (!isNaN(date.getTime())) {
            featuredDate = date;
          }
        }

        // Also calculate the earliest valid class as fallback
        if (!cls.isFull && !cls.isWaitlistOnly && cls.startDate) {
          const date = new Date(cls.startDate);
          if (!isNaN(date.getTime()) && date > new Date('2025-01-01')) { 
             if (!earliestValidDate || date < earliestValidDate) {
               earliestValidDate = date;
             }
          }
        }
      });
    });

    // Prefer featured date if it exists, otherwise use earliest
    const finalDate = featuredDate || earliestValidDate;

    if (finalDate) {
      return finalDate.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      });
    }

    return lang === 'en' ? 'Dates coming soon' : 'Fechas próximamente';
  };

  const title = lang === 'en' ? 'Upcoming Classes' : 'Próximas Clases';
  const subtitle = lang === 'en' ? 'Spots are filling up quickly. Apply today to secure your seat!' : '¡Los cupos se llenan rápido. Aplica hoy para asegurar tu lugar!';
  
  const classes = [
    {
      campus: 'Gastonia Campus',
      date: loading ? '...' : getNextAvailableDate('gastonia'),
      type: '12-Week Program'
    },
    {
      campus: 'Lake Norman Campus',
      date: loading ? '...' : getNextAvailableDate('lakeNorman'),
      type: '12-Week Program'
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6">
            {title}
          </h2>
          <p className="text-lg text-brand-primary font-bold max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {classes.map((cls, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 hover:border-brand-primary/30 transition-all group relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <CalendarDays className="w-24 h-24 text-brand-primary" />
              </div>
              
              <div className="relative z-10">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-primary/10 rounded-full text-brand-primary font-bold text-sm mb-6">
                  <MapPin className="w-4 h-4" />
                  {cls.campus}
                </div>
                
                <h3 className="text-3xl font-black text-slate-900 mb-2">
                  {cls.date}
                </h3>
                
                <div className="flex items-center gap-2 text-slate-500 font-medium mb-8">
                  <Clock className="w-5 h-5" />
                  <span>{cls.type}</span>
                </div>

                <button 
                  onClick={() => navigate('/application')}
                  className="w-full flex items-center justify-center gap-2 py-4 bg-slate-50 hover:bg-brand-primary text-slate-900 hover:text-white rounded-2xl font-bold transition-all group-hover:shadow-lg"
                >
                  {lang === 'en' ? 'Enroll Today' : 'Inscríbete Hoy'}
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NextClasses;
